import GuideLayout from "./GuideLayout";

export default function ConvertEpsToPdf() {
  return (
    <GuideLayout
      title="How to Convert EPS to PDF"
      description="Convert EPS to PDF to get a universal, print-ready file that keeps vectors sharp. Learn what is preserved, why the page size comes out odd, how fonts behave, and what print shops actually want."
      path="/guides/convert-eps-to-pdf"
      updated="August 1, 2026"
      related={["/guides/convert-eps-to-png", "/guides/eps-vs-svg-vs-ai-vs-pdf", "/guides/convert-eps-to-jpg"]}
    >
      <p className="lead">
        PDF is the universal delivery format. Converting an EPS to PDF gives you a file
        that opens on virtually any device while keeping the vector artwork perfectly
        crisp. Unlike converting to PNG or JPG, this conversion loses essentially
        nothing — but there are a few quirks around page size and fonts that catch
        people out.
      </p>

      <h2>Why this conversion is lossless</h2>
      <p>
        EPS and PDF are close relatives. EPS is a constrained dialect of PostScript, and
        PDF was designed by Adobe as PostScript&apos;s successor. Converting between them
        is largely a matter of rewriting the same drawing instructions into a newer
        container, so:
      </p>
      <ul>
        <li><strong>Vectors stay vectors.</strong> Curves and shapes remain mathematical, so the file is still sharp at any size — on a business card or a billboard.</li>
        <li><strong>No resolution is chosen.</strong> Nothing is rasterized, so there is no DPI decision to get wrong and no quality setting to regret.</li>
        <li><strong>Colour values carry across.</strong> CMYK stays CMYK and spot colours are preserved, which matters for print.</li>
      </ul>
      <p>
        This is the key difference from{" "}
        <a href="/guides/convert-eps-to-png">converting to PNG</a> or{" "}
        <a href="/guides/convert-eps-to-jpg">JPG</a>, both of which flatten the artwork to
        a fixed grid of pixels. If you are sending artwork to anyone who might need to
        resize or print it, PDF is the right choice.
      </p>

      <h2>Why convert at all?</h2>
      <ul>
        <li><strong>It opens everywhere.</strong> Every phone, laptop and browser can display a PDF. Almost nothing opens an EPS without extra software.</li>
        <li><strong>Print shops prefer it.</strong> Most modern workflows are built around PDF, and some no longer accept EPS at all.</li>
        <li><strong>Fonts can be embedded.</strong> PDF carries the actual font data, so text renders identically on someone else&apos;s machine.</li>
        <li><strong>It supports real transparency.</strong> EPS does not, which is why drop shadows and glows are flattened when an EPS is written.</li>
        <li><strong>The recipient can preview it.</strong> No more emailing a logo that the client physically cannot open.</li>
      </ul>

      <h2>How to convert EPS to PDF</h2>
      <h3>Online with EPS Viewer (easiest)</h3>
      <ol>
        <li>Open the <a href="/">EPS Viewer</a> and upload your <code>.eps</code> file (or click <strong>Try a sample</strong>).</li>
        <li>Wait for the preview to render.</li>
        <li>Click the <strong>PDF</strong> button below the preview to download a print-ready PDF.</li>
      </ol>
      <p>
        The result is a true vector PDF, not a photograph of the artwork, so it stays
        sharp at any size. Nothing is installed, and it behaves the same on Windows, Mac,
        Linux and mobile.
      </p>

      <h3>With Inkscape (free, offline)</h3>
      <ol>
        <li>Open Inkscape and choose <strong>File → Import</strong>, then select your EPS.</li>
        <li>Go to <strong>File → Save As</strong> and pick <strong>PDF</strong> as the format.</li>
        <li>
          In the export dialog, leave text as text if you want it selectable, or tick the
          option to convert text to paths if the recipient may not have your fonts.
        </li>
      </ol>
      <p>
        Inkscape needs Ghostscript installed to read EPS. If the import produces an empty
        canvas, that is almost always the missing piece.
      </p>

      <h3>With Ghostscript (command line)</h3>
      <p>
        Ghostscript ships with <code>epstopdf</code>, which is the cleanest option for
        batches: <code>epstopdf input.eps</code> writes <code>input.pdf</code> beside it.
        For more control, call Ghostscript directly:
      </p>
      <p>
        <code>gs -dNOPAUSE -dBATCH -sDEVICE=pdfwrite -dEPSCrop -sOutputFile=out.pdf in.eps</code>
      </p>
      <p>
        The <code>-dEPSCrop</code> flag is the important one — without it, Ghostscript
        drops your artwork onto a default US Letter page and you get a large white
        margin.
      </p>

      <h2>Why the page size looks wrong</h2>
      <p>
        This is the most common complaint, and it is not really an error. An EPS has no
        page — it has a <code>%%BoundingBox</code> comment near the top of the file
        declaring the artwork&apos;s extents in points (1 point = 1/72 inch). Converters
        turn that bounding box into the PDF page size.
      </p>
      <p>So a logo 200 points wide becomes a PDF page 200 points wide, with no margins. Two things follow:</p>
      <ul>
        <li>
          <strong>Unexpected page dimensions</strong> are normal. If you need the artwork
          centred on A4 or Letter, place the PDF into a document in Illustrator, InDesign
          or even Word rather than expecting the converter to guess.
        </li>
        <li>
          <strong>Clipped edges</strong> mean the bounding box is wrong — usually because
          a stroke, glow or shadow extends past the declared box. Reopen the original in a
          vector editor and re-save so the bounding box is recalculated.
        </li>
      </ul>

      <h2>What happens to fonts</h2>
      <p>
        PDF can embed fonts, but conversion cannot invent data the EPS never contained.
        There are three cases:
      </p>
      <ul>
        <li><strong>Text was outlined before the EPS was made.</strong> The letters are already vector shapes. Everything renders perfectly, but the text is no longer editable or selectable. This is the safest state for a logo.</li>
        <li><strong>Fonts were embedded in the EPS.</strong> They carry through to the PDF and render correctly anywhere.</li>
        <li><strong>Fonts were neither embedded nor outlined.</strong> The converter substitutes whatever is available, so spacing shifts, weights change, or text appears as boxes. The PDF then permanently records the substitution.</li>
      </ul>
      <p>
        If your text looks wrong after conversion, the problem started in the EPS, not in
        the conversion. Ask for a version with text outlined — in Illustrator that is
        Type → Create Outlines, in Inkscape Path → Object to Path. See{" "}
        <a href="/guides/fix-eps-file-problems">common EPS problems</a> for more.
      </p>

      <h2>Sending the PDF to a print shop</h2>
      <p>
        A converted PDF is usually accepted as-is for simple jobs, but commercial
        printing has extra requirements that no automatic conversion can add for you:
      </p>
      <div className="table-wrap">
        <table className="tbl">
          <thead><tr><th>Requirement</th><th>What it means</th></tr></thead>
          <tbody>
            <tr><td>Bleed</td><td>3&nbsp;mm of artwork extending past the trim edge, so trimming never leaves a white sliver.</td></tr>
            <tr><td>CMYK colour</td><td>Presses print in CMYK. RGB files get converted anyway, often with a visible shift.</td></tr>
            <tr><td>Embedded fonts</td><td>Or text outlined, so the printer&apos;s machine reproduces your typography exactly.</td></tr>
            <tr><td>PDF/X-1a or PDF/X-4</td><td>Print-specific PDF profiles many shops require. Exported from Illustrator or InDesign, not from a plain converter.</td></tr>
            <tr><td>300 DPI images</td><td>Applies only to photographs embedded inside the artwork — pure vectors have no DPI.</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        For a logo, a business card or a flyer, converting and sending is generally fine.
        For packaging, large-format or anything with bleed, go back to the original design
        file and export properly.
      </p>

      <h2>Troubleshooting</h2>
      <ul>
        <li><strong>Blank PDF:</strong> the EPS may be damaged, or the converter lacked a PostScript interpreter. Try the online viewer first to confirm the file renders at all.</li>
        <li><strong>Huge file size:</strong> the EPS contains embedded high-resolution photographs. Downsample the images in the original artwork.</li>
        <li><strong>Colours look duller than on screen:</strong> the file is CMYK, which is correct for print. Screens show RGB, which is a wider range.</li>
        <li><strong>Artwork appears rasterized when zoomed:</strong> the EPS itself contained a bitmap rather than vectors. No conversion can turn pixels back into curves.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      <dl className="guide-faq">
        <dt>Does converting EPS to PDF lose quality?</dt>
        <dd>No. Both are vector formats from the same PostScript lineage, so the artwork stays sharp and scalable at any size.</dd>
        <dt>Is a converted PDF editable?</dt>
        <dd>Yes, in Illustrator, Inkscape or Affinity Designer. Text is only editable if it was not outlined first.</dd>
        <dt>Why is my PDF page a strange size?</dt>
        <dd>The EPS bounding box becomes the page size, so the page matches the artwork exactly with no margins. Place it into a document if you need a standard paper size.</dd>
        <dt>Should I send my printer an EPS or a PDF?</dt>
        <dd>PDF, in almost all cases. Most modern print workflows prefer it, and some no longer accept EPS. Ask your printer if they need a specific PDF/X profile.</dd>
        <dt>Just need to look at the file?</dt>
        <dd>Use the <a href="/">online viewer</a> and download a PNG — that is quicker than producing a PDF you do not need.</dd>
      </dl>
    </GuideLayout>
  );
}
